import { useEffect, useMemo, useState } from "react";
import { Lock, ExternalLink, ChevronDown, ChevronRight, Monitor } from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { projectDisplayName } from "../ui";
import { useT } from "../i18n";
import { katalog } from "../i18n/lang";

interface GanttItem {
  id: string;
  project_id: string | null;
  title: string;
  status: string | null;
  start_date: string;
  due_date: string;
  source: "local" | "salesforce";
  sf_url: string | null;
  read_only: boolean;
}

interface ProjectView {
  id: string;
  name: string;
  source: "local" | "salesforce";
}

const RANGE_DAYS_FORWARD = 180;
const RANGE_DAYS_BACK = 90;

// Aufgaben ohne echtes Enddatum bekommen im Backend ein Sentinel-Datum
// (z. B. 2099-12-31). Alles ab diesem Jahr gilt als „offen ohne Enddatum" —
// wir rendern den Balken dann nur bis heute + OPEN_TAIL_DAYS mit
// ausgefranstem Ende statt bis 2099.
const OPEN_END_SENTINEL_YEAR = 2098;
const OPEN_TAIL_DAYS = 14;

function dayDiff(a: string | Date, b: string | Date): number {
  const x = typeof a === "string" ? new Date(a) : a;
  const y = typeof b === "string" ? new Date(b) : b;
  return Math.round((y.getTime() - x.getTime()) / 86_400_000);
}

function isOpenEnded(due: string | null): boolean {
  if (!due) return true;
  const d = new Date(due);
  return Number.isNaN(d.getTime()) || d.getFullYear() >= OPEN_END_SENTINEL_YEAR;
}

// Lokalisiertes Kurz-Datum (de-DE), z. B. „27.01.2025".
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-DE");
}

// Rohe (teils SF-)Status-Werte auf Anzeige-Labels mappen. Die Zeitachse hat
// eigene Texte (z. B. „Laufend" statt „Läuft") — deshalb ein eigener Abschnitt
// im Katalog und nicht ui/labels.ts. Ausserhalb einer Komponente aufgerufen,
// deshalb `katalog()` statt `useT()`; die Seite selbst hängt am Sprach-Abo und
// rendert bei einem Wechsel neu.
function statusText(status: string | null): string {
  const s = (status ?? "").toLowerCase();
  const map = katalog().tasks.zeitachse.status as Record<string, string>;
  return map[s] ?? (status ?? "?");
}

// Balkenfarbe rein über semantische Tokens: fertig = success, laufend = run
// (Akzent), verloren = danger, Salesforce-Spiegel = warning, alles andere
// (neu/offen) = neutraler Grauton.
function statusColor(status: string | null, source: string): string {
  const s = (status ?? "").toLowerCase();
  if (s === "done" || s === "closed") return "bg-success";
  if (s === "lost") return "bg-danger";
  if (s === "in_progress" || s === "ongoing") return "bg-run";
  if (source === "salesforce") return "bg-warning";
  return "bg-text-3";
}

export function GanttView({ embedded = false }: { embedded?: boolean } = {}) {
  const t = useT();
  const [items, setItems] = useState<GanttItem[]>([]);
  const [projects, setProjects] = useState<ProjectView[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [hideClosed, setHideClosed] = useState(true);

  useEffect(() => {
    api.get<GanttItem[]>("/views/gantt").then(setItems).catch(() => setItems([]));
    api
      .get<{ projects: ProjectView[] }>("/views/projects?scope=all")
      .then((r) => setProjects(r.projects))
      .catch(() => {});
  }, []);

  const { rangeStart, rangeEnd, totalDays } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - RANGE_DAYS_BACK);
    const end = new Date(now);
    end.setDate(end.getDate() + RANGE_DAYS_FORWARD);
    return { rangeStart: start, rangeEnd: end, totalDays: dayDiff(start, end) };
  }, []);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (hideClosed) {
        const s = (it.status ?? "").toLowerCase();
        if (s === "done" || s === "closed" || s === "lost") return false;
      }
      if (projectFilter !== "all" && it.project_id !== projectFilter) return false;
      const due = new Date(it.due_date);
      const start = new Date(it.start_date);
      if (due < rangeStart || start > rangeEnd) return false;
      return true;
    });
  }, [items, hideClosed, projectFilter, rangeStart, rangeEnd]);

  const projectName = (id: string | null): string => {
    if (!id) return t.tasks.zeitachse.ohneProjekt;
    const p = projects.find((x) => x.id === id);
    return projectDisplayName(p?.name) || id;
  };

  const grouped: Record<string, GanttItem[]> = {};
  for (const it of filtered) {
    const key = it.project_id ?? "_orphan";
    (grouped[key] = grouped[key] ?? []).push(it);
  }
  const groupKeys = Object.keys(grouped).sort((a, b) =>
    projectName(a === "_orphan" ? null : a).localeCompare(
      projectName(b === "_orphan" ? null : b)
    )
  );

  const months: { label: string; offsetPct: number }[] = useMemo(() => {
    const out: { label: string; offsetPct: number }[] = [];
    const cursor = new Date(rangeStart);
    cursor.setDate(1);
    while (cursor <= rangeEnd) {
      const off = (dayDiff(rangeStart, cursor) / totalDays) * 100;
      out.push({
        label: cursor.toLocaleString("de-DE", { month: "short", year: "2-digit" }),
        offsetPct: Math.max(0, off),
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return out;
  }, [rangeStart, rangeEnd, totalDays]);

  const todayOffsetPct = (dayDiff(rangeStart, new Date()) / totalDays) * 100;

  const toggle = (key: string) =>
    setCollapsed((s) => ({ ...s, [key]: !s[key] }));

  return (
    <>
      {/* Mobile-Hint: Drei-Pane-Gantt ist on-purpose Desktop. Auf <768px
          zeigen wir eine ehrliche Card mit Begruendung + Alternativ-Links
          zu Kalender + Kanban (die haben mobile-Mode). */}
      <div className="md:hidden h-full flex items-center justify-center p-6 bg-cream-50 dark:bg-ink-900">
        <div className="max-w-sm w-full bg-surface dark:bg-ink-800 border border-cream-200 dark:border-ink-700 rounded-lg shadow-sm p-6 text-center space-y-3">
          <Monitor className="w-10 h-10 text-brand-500 mx-auto" />
          <h2 className="text-lg font-display font-semibold text-ink">
            {t.tasks.zeitachse.mobilTitel}
          </h2>
          <p className="text-sm text-ink-50/70 leading-relaxed">
            {t.tasks.zeitachse.mobilText}
          </p>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <a
              href="/kanban"
              className="min-h-11 px-3 py-2 rounded-md bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium flex items-center justify-center"
            >
              {t.tasks.zeitachse.zuKanban}
            </a>
            <a
              href="/calendar"
              className="min-h-11 px-3 py-2 rounded-md border border-cream-300 dark:border-ink-600 hover:bg-cream-100 dark:hover:bg-ink-700 text-sm font-medium flex items-center justify-center text-ink"
            >
              {t.tasks.zeitachse.zuKalender}
            </a>
          </div>
          <p className="text-[11px] text-ink-50/50 pt-2">
            {t.tasks.zeitachse.mobilFuss}
          </p>
        </div>
      </div>

    <div className="hidden md:flex h-full flex-col">
      <header className="px-6 py-4 border-b border-border bg-surface flex flex-wrap items-end justify-between gap-3">
        <div>
          {!embedded && <h1 className="text-2xl font-semibold">{t.tasks.zeitachse.titel}</h1>}
          <p className="text-xs text-text-2 mt-1">
            {t.tasks.zeitachse.kopf(
              filtered.length,
              rangeStart.toLocaleDateString("de-DE"),
              rangeEnd.toLocaleDateString("de-DE")
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            aria-label={t.tasks.zeitachse.projektFilter}
            className="bg-cream-50 dark:bg-ink-800 border border-border rounded-md px-2 py-1 focus:outline-none"
          >
            <option value="all">{t.tasks.zeitachse.alleProjekte}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {projectDisplayName(p.name)}
              </option>
            ))}
          </select>
          <label className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={hideClosed}
              onChange={(e) => setHideClosed(e.target.checked)}
            />
            {t.tasks.zeitachse.erledigteAusblenden}
          </label>
        </div>
      </header>

      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-[280px_1fr] sticky top-0 z-10 bg-surface border-b border-border">
          <div className="px-4 py-2 text-xs uppercase tracking-wider text-text-3">
            {t.tasks.zeitachse.spalteProjektAufgabe}
          </div>
          <div className="relative h-8">
            {months.map((m) => (
              <div
                key={m.label}
                className="absolute top-0 border-l border-border h-full text-[10px] text-text-2 pl-1"
                style={{ left: `${m.offsetPct}%` }}
              >
                {m.label}
              </div>
            ))}
            <div
              className="absolute top-0 bottom-0 w-px bg-brand-500/60"
              style={{ left: `${todayOffsetPct}%` }}
              title={t.tasks.zeitachse.heute}
            />
          </div>
        </div>

        {groupKeys.length === 0 && (
          <div className="text-center py-16 text-sm text-text-2">
            {t.tasks.zeitachse.leer}
          </div>
        )}

        {groupKeys.map((key) => {
          const tasks = grouped[key];
          const isCollapsed = collapsed[key];
          const proj = projects.find((p) => p.id === key);
          return (
            <div key={key} className="border-b border-border">
              <button
                onClick={() => toggle(key)}
                className="w-full grid grid-cols-[280px_1fr] hover:bg-surface-2"
              >
                <div className="px-4 py-2 text-left flex items-center gap-2 text-sm font-medium">
                  {isCollapsed ? (
                    <ChevronRight className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                  {proj?.source === "salesforce" && (
                    <Lock className="w-3 h-3 text-warning" />
                  )}
                  <span className="truncate">
                    {projectName(key === "_orphan" ? null : key)}
                  </span>
                  <span className="text-xs text-text-2 font-normal">
                    {tasks.length}
                  </span>
                </div>
                <div />
              </button>
              {!isCollapsed &&
                tasks.map((item) => {
                  const openEnded = isOpenEnded(item.due_date);
                  // Offene Aufgaben laufen nicht bis 2099, sondern bis
                  // heute + OPEN_TAIL_DAYS mit ausgefranstem Ende.
                  const effectiveEnd = openEnded
                    ? new Date(Date.now() + OPEN_TAIL_DAYS * 86_400_000)
                    : new Date(item.due_date);
                  // Beide Enden auf [0,100] klippen, damit Aufgaben die vor
                  // rangeStart beginnen (oder nach rangeEnd enden) nicht über
                  // den sichtbaren Bereich hinausmalen.
                  const startOffRaw =
                    (dayDiff(rangeStart, item.start_date) / totalDays) * 100;
                  const endOffRaw =
                    (dayDiff(rangeStart, effectiveEnd) / totalDays) * 100;
                  const barLeft = Math.max(0, Math.min(100, startOffRaw));
                  const barRight = Math.max(0, Math.min(100, endOffRaw));
                  const barWidth = Math.max(0.5, barRight - barLeft);
                  const barTitle = openEnded
                    ? t.tasks.zeitachse.balkenOffen(item.title, fmtDate(item.start_date))
                    : t.tasks.zeitachse.balken(
                        item.title,
                        fmtDate(item.start_date),
                        fmtDate(item.due_date)
                      );
                  return (
                    <div
                      key={item.id}
                      className="grid grid-cols-[280px_1fr] hover:bg-surface-2 border-t border-border"
                    >
                      <div className="px-4 py-1.5 pl-9">
                        <div className="text-sm truncate flex items-center gap-1.5">
                          {item.read_only && (
                            <Lock className="w-3 h-3 text-warning shrink-0" />
                          )}
                          <span className="truncate">{item.title}</span>
                          {item.sf_url && (
                            <a
                              href={item.sf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <div className="text-[11px] text-text-2">
                          {fmtDate(item.start_date)} →{" "}
                          {openEnded ? (
                            <span className="italic text-text-3">
                              {t.tasks.zeitachse.offen}
                            </span>
                          ) : (
                            fmtDate(item.due_date)
                          )}{" "}
                          · {statusText(item.status)}
                        </div>
                      </div>
                      <div className="relative h-7 my-1 mx-2">
                        <div
                          className={clsx(
                            "absolute top-1.5 h-4 rounded-md shadow-sm",
                            statusColor(item.status, item.source),
                            // Offenes Ende: Balken franst nach rechts aus
                            // (Gradient-Fade via mask) statt harter Kante.
                            openEnded &&
                              "[mask-image:linear-gradient(to_right,black_55%,transparent_100%)]"
                          )}
                          style={{
                            left: `${barLeft}%`,
                            width: `${barWidth}%`,
                          }}
                          title={barTitle}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
    </>
  );
}

/** Direkt-Route-Hülle (Bestand). Der Inhalt lebt in <GanttView/>. */
export default function GanttPage() {
  return <GanttView />;
}
